import React, { Suspense, lazy, useEffect } from 'react'
import {
  AnonSessionRoute,
  AuthRoute,
  NotFound,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useQueryParams,
} from '@fs/zion-router'
import { targetEnv } from '@fs/zion-config'
import ErrorBoundary from '@fs/zion-error-boundary'
import { RecordAuthRoute } from '@fs/zion-gedcomx'
import { LoadingSkeleton as RecordLoadingSkeleton } from '@fs/zion-record-details'
import Cookies from 'js-cookie'
import { useContainerWidth } from '@fs/zion-ui'

import { useUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import useMeasure from 'react-use-measure'
import UnknownError from './components/error-components/UnknownError'
import NavigationTabs from './components/hr/NavigationTabs'
import lightningjs from './lightningjs'
import { extractArk } from './components/film-viewer/useFilmData'
import CenteredSpinner from './components/shared/CenteredSpinner'

const Suspend = ({ children }) => <Suspense fallback={<CenteredSpinner />}>{children}</Suspense>

/**
 * This will retry failed chunks up to 5 times
 * Hopefully it will decrease Sentry errors
 * @param {Function} lazyComponent - lazy component import function
 * @param {Number} attemptsLeft - number of attempts to make (defaults to 5)
 * @returns {Promise<any>} Promise that rejects if all additional attempts fail.
 */
function componentLoader(lazyComponent, attemptsLeft = 5) {
  return new Promise((resolve, reject) => {
    lazyComponent()
      .then(resolve)
      .catch((error) => {
        // let us retry after 100 ms
        setTimeout(() => {
          if (attemptsLeft === 1) {
            reject(error)
            return
          }
          componentLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject)
        }, 100)
      })
  })
}

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy
const CatalogDetails = lazy(() =>
  componentLoader(() => import('./components/catalog/catalog-details/CatalogDetailsPage'))
)
const CatalogResultsPage = lazy(() => componentLoader(() => import('./components/catalog/CatalogResultsPage')))
const CatalogSearchPage = lazy(() => componentLoader(() => import('./components/catalog/CatalogSearchPage')))
const CatalogPrintListPage = lazy(() => componentLoader(() => import('./components/catalog/CatalogPrintListPage')))
const JiapuAttach = lazy(() => componentLoader(() => import('./components/prototypes/jiapu-attach/JiapuAttach')))

const CollectionDetails = lazy(() => componentLoader(() => import('./components/hr/CollectionDetailsPage')))
const CollectionListPage = lazy(() => componentLoader(() => import('./components/hr/CollectionListPage')))
const FilmDebugPage = lazy(() => componentLoader(() => import('./components/prototypes/film-debug/FilmDebugPage')))
const GenCollectionDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenCollectionDetailsPage')))
const GenSearchPage = lazy(() => componentLoader(() => import('./components/gen/GenSearchPage')))
const GenResultsPage = lazy(() => componentLoader(() => import('./components/gen/GenResultsPage')))
const GenDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenDetailsPage')))
const GenSubmissionsPage = lazy(() => componentLoader(() => import('./components/gen/GenSubmissionsPage')))
const GenSubmissionDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenSubmissionDetailsPage')))
const HRComparePage = lazy(() => componentLoader(() => import('./components/prototypes/hr/HRComparePage')))
const LocationListPage = lazy(() => componentLoader(() => import('./components/location/LocationListPage')))
const LocationPage = lazy(() => componentLoader(() => import('./components/location/LocationPage')))
const RecordDebugPage = lazy(() =>
  componentLoader(() => import('./components/prototypes/record-debug/RecordDebugPage'))
)
const RecordDetailsPage = lazy(() => componentLoader(() => import('./components/hr/RecordDetailsPage')))
const RecordRedirectToPersona = lazy(() => componentLoader(() => import('./components/hr/RecordRedirectToPersona')))
const SearchHomePage = lazy(() => componentLoader(() => import('./components/hr/SearchHomePage')))
const SearchResultsPage = lazy(() => componentLoader(() => import('./components/hr/SearchResultsPage')))
const SearchResultsLoadingSkeleton = lazy(() =>
  componentLoader(() => import('@fs/zion-record-search').then((module) => ({ default: module.LoadingSkeletons })))
)
const OldSourceLinkerPage = lazy(() => componentLoader(() => import('./components/old-sourcelinker/SourceLinkerPage')))
const SourceLinkerPage = lazy(() => componentLoader(() => import('./components/sourcelinker/SourceLinkerPage')))

const TreeSearchPage = lazy(() => componentLoader(() => import('./components/tree/TreeSearchPage')))
const TreeResultsPage = lazy(() => componentLoader(() => import('./components/tree/TreeResultsPage')))
const TreeCETDetailsPage = lazy(() => componentLoader(() => import('./components/tree/TreeCETDetailsPage')))

const FilmViewerPage = lazy(() => componentLoader(() => import('./components/film-viewer/FilmViewerPage')))

const FullTextSearchPage = lazy(() => componentLoader(() => import('./components/full-text-search/FullTextSearchPage')))
const FullTextResultsPage = lazy(() =>
  componentLoader(() => import('./components/full-text-search/FullTextResultsPage'))
)

const EMBEDDED_MOBILE_COOKIE_NAME = 'embeddedMobile'

function App({ setUseFatFooter }) {
  // Hide NavigationTabs if page is embedded in mobile app WebView
  const embeddedMobile = !!Cookies.get(EMBEDDED_MOBILE_COOKIE_NAME)
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, xl: false })
  const hideSubNav = isMobile || embeddedMobile
  const useRetSearch = useFeatureFlag('search_CETSearch')?.isOn
  const useRSLStateRefactor = useFeatureFlag('search_rslStateRefactor')?.isOn

  const { signedIn } = useUser()
  const { pathname } = useLocation()
  const homePage = '/search/'

  useEffect(() => {
    // eslint-disable-next-line babel/no-unused-expressions
    window.lightningjs || lightningjs

    if (pathname.match(/\/location\/+\/+/i)) {
      window.usabilla_live = window.lightningjs.require('usabilla_live', '//w.usabilla.com/08c3d906fb80.js')
      window.usabilla_live('virtualPageView')
    }

    if (pathname === homePage && !signedIn) {
      setUseFatFooter(true)
    } else {
      setUseFatFooter(false)
    }
  }, [homePage, pathname, setUseFatFooter, signedIn])

  const query = useHistory()?.location?.search || ''
  const {
    query: { url, bounds, uri },
  } = useQueryParams()
  const filmViewerHighlightRedirectUrl = getFilmViewerHighlightRedirectUrl(url, bounds, pathname)
  const filmViewerRedirectUri = uri ? getFilmViewerRedirectUri(uri) : ''

  const [navRef, navBounds] = useMeasure()
  const subnavHeight = Math.ceil(navBounds?.height || 0)

  // "Switch" only switches on direct children, so routes and redirects have to be direct children.
  return (
    <div style={{ '--subnav-height': `${subnavHeight}px`, height: '100%' }}>
      <ErrorBoundary FallbackComponent={UnknownError}>
        <Suspense fallback={<CenteredSpinner />}>
          {!hideSubNav && <NavigationTabs navRef={navRef} />}

          {
            <Switch>
              {/* Film Viewer Routes */}
              {/* 'waypoint-chooser' mode - waypoint viewer, /search/image/index */}
              <RecordAuthRoute
                path="/search/image/index"
                component={FilmViewerPage}
                LoadingSkeleton={<CenteredSpinner />}
              />
              {/* 'dgs' mode - film viewer, /search/film/:dgsNum */}
              <RecordAuthRoute
                path="/search/film/:dgsNum"
                component={FilmViewerPage}
                LoadingSkeleton={<CenteredSpinner />}
              />
              {/* 'ark' mode - ark viewer, /ark:/61903/:arkPath */}
              <RecordAuthRoute
                path="/ark\:/61903/3\:(1|2)\::arkPath"
                component={FilmViewerPage}
                LoadingSkeleton={<CenteredSpinner />}
              />
              {/* redirect to an image with a `bounds` (highlights) query param */}
              {filmViewerHighlightRedirectUrl && (
                <Redirect
                  from="/search/image/highlight"
                  exact
                  to={`${filmViewerHighlightRedirectUrl.replace(/:/g, '\\:')}`}
                />
              )}
              {/* redirect to the `uri` param */}
              {filmViewerRedirectUri && (
                // the `replace` escapes `:` with `\:` so that React Router does not treat the ark as a match param
                <Redirect from="/search/image/viewer" exact to={`${filmViewerRedirectUri.replace(/:/g, '\\:')}`} />
              )}
              {/* empty landing - not a useful page - old film viewer was receiving traffic to this so we want it too */}
              <AnonSessionRoute
                path="/search/image/viewer"
                exact
                component={FilmViewerPage}
                LoadingSkeleton={<CenteredSpinner />}
              />
              {/* Full-Text Search Routes */}
              <AuthRoute path="/search/full-text/results" component={FullTextResultsPage} />
              <AuthRoute path="/search/full-text" component={FullTextSearchPage} />
              {/* Redirect for localhost convenience only */}
              {targetEnv === 'local' && <Redirect from="/" exact to="/search/" />}
              {/* Search Home */}
              <AnonSessionRoute path="/search/" exact LoadingComponent={CenteredSpinner}>
                <Suspend>
                  <SearchHomePage />
                </Suspend>
              </AnonSessionRoute>
              {/* Record Details */}
              <RecordAuthRoute
                path="/ark\:/61903/1\:1\::arkPath"
                component={RecordDetailsPage}
                LoadingSkeleton={<RecordLoadingSkeleton />}
              />
              {/* Ark 1:2 paths are long-lived URLs for records, but full records don't have dedicated pages. */}
              {/* Instead, we redirect users to the first 1:1 persona in the record. */}
              {/* This route finds the first persona and does a redirect. */}
              {/* If the user doesn't have permission to see the record, we redirect to a login instead. */}
              <AnonSessionRoute path="/ark\:/61903/1\:2\::arkPath" component={RecordRedirectToPersona} />

              {/* These location redirects are required BEFORE the HR collections paths */}
              <Redirect from="/search/collection/location/:region" exact to={`/search/location/:region?${query}`} />
              <Redirect
                from="/search/collection/location/:region/:subRegion"
                exact
                to={`/search/location/:region/:subRegion?${query}`}
              />
              {/* HR Collections */}
              <AnonSessionRoute path="/search/collection/list" component={CollectionListPage} />
              <AnonSessionRoute path="/1940census" component={CollectionDetails} />
              <AnonSessionRoute path="/search/collection/:collectionId" component={CollectionDetails} />
              {/* Locations */}
              <AnonSessionRoute path="/search/location/list" exact component={LocationListPage} />
              <AnonSessionRoute path="/search/location/:region" exact component={LocationPage} />
              <AnonSessionRoute path="/search/location/:region/:subRegion" exact component={LocationPage} />
              {/* HR Search Results */}
              <RecordAuthRoute
                path="/search/record/results"
                component={SearchResultsPage}
                LoadingSkeleton={<SearchResultsLoadingSkeleton />}
              />
              {/* Tree Search & Results */}
              <AuthRoute path="/search/tree/results" component={TreeResultsPage} />
              <AnonSessionRoute path="/search/tree" exact component={TreeSearchPage} />
              <AnonSessionRoute path="/search/tree/:searchType" exact component={TreeSearchPage} />
              {useRetSearch && (
                <AnonSessionRoute path="/search/tree/:collectionId/:treeId" component={TreeCETDetailsPage} />
              )}
              {/* Genealogies */}
              {<AnonSessionRoute path="/search/genealogies" exact component={GenSearchPage} />}
              <AnonSessionRoute path="/search/genealogies/results" component={GenResultsPage} />
              <AnonSessionRoute
                path="/search/genealogies/collection/:collectionId"
                component={GenCollectionDetailsPage}
              />
              <AnonSessionRoute path="/search/genealogies/submissions" component={GenSubmissionsPage} />
              <AnonSessionRoute
                path="/search/genealogies/submission/:collectionId/:submissionId"
                component={GenSubmissionDetailsPage}
              />
              <AnonSessionRoute path="/search/cgt" exact component={GenSubmissionsPage} />
              {/* Source Linker */}
              {
                <AuthRoute
                  path="/search/linker"
                  component={useRSLStateRefactor ? SourceLinkerPage : OldSourceLinkerPage}
                />
              }
              {/* Genealogies Details */}
              <AnonSessionRoute path="/ark\:/61903/:arkPath" component={GenDetailsPage} />
              {/* Redirects for genealogies pages */}
              <Redirect exact from="/search/family-trees" to={`/search/genealogies${query}`} />
              <Redirect exact from="/search/family-trees/results" to={`/search/genealogies/results${query}`} />
              {/* Old routes for genealogies pages */}
              {<Route path="/search/family-trees" exact component={GenSearchPage} requirePersonalData />}
              <AnonSessionRoute path="/search/family-trees/results" component={GenResultsPage} requirePersonalData />
              {/* Catalog Search & Results */}
              <AnonSessionRoute path="/search/catalog/results" component={CatalogResultsPage} requirePersonalData />
              <AnonSessionRoute path="/search/catalog" exact component={CatalogSearchPage} requirePersonalData />
              {/* Catalog Print List */}
              <AnonSessionRoute path="/search/catalog/print-list" component={CatalogPrintListPage} />
              {/* Catalog Details */}
              <AnonSessionRoute path="/search/catalog(/oclc)?/:catId" component={CatalogDetails} />
              {/* hidden routes; users shouldn't visit these */}
              <AnonSessionRoute path="/search/filmdebug" component={FilmDebugPage} />
              <AuthRoute path="/search/hrcompare" component={HRComparePage} />
              <AnonSessionRoute path="/search/recorddebug" component={RecordDebugPage} />
              <Route path="/search/blank" component={() => 'This page is intentionally left blank'} />
              <AuthRoute path="/search/jiapu-attach" component={JiapuAttach} />

              {/* no route found */}
              <Route component={NotFound} />
            </Switch>
          }
        </Suspense>
      </ErrorBoundary>
    </div>
  )
}

/**
 * Get the redirect URL for the film viewer highlight when a bounds (highlights) url param is used.
 * This is used to redirect the user to the film viewer with the highlight bounds.
 * @param {string} url - The URL query param (e.g. "https://www.familysearch.org/dz/v1/3:1:3QS7-L9WZ-399R/image.xml").
 * @param {string} bounds - The bounds query param (e.g. "[1011,874:1216,918][510,1397:725,1470][1219,874:1431,918]").
 * @param {string} pathname - The pathname of the current URL.
 * @returns {string} - The redirect URL for the film viewer highlight (e.g. `/ark:/61903/3:1:S3HY-D1C8-K1?bounds=[1011,874:1216,918][510,1397:725,1470][1219,874:1431,918]&boundsArk=3:1:3QS7-L9WZ-399R`).
 */
function getFilmViewerHighlightRedirectUrl(url, bounds, pathname) {
  if (url && pathname === '/search/image/highlight') {
    const ark = extractArk(url)
    return `/ark:/61903/${ark}?bounds=${bounds}&boundsArk=${ark}`
  }
  return ''
}

/**
 * Get the redirect URI for the film viewer.
 *
 * @param {string} uri - The URI query param (e.g. `https://www.familysearch.org/ark:/61903/3:1:S3HY-D1C8-K1?i=466&i=466&cc=1951739`).
 * @returns {string} - The redirect URI for the film viewer (e.g. `/ark:/61903/3:1:S3HY-D1C8-K1?i=466`).
 */
function getFilmViewerRedirectUri(uri) {
  if (uri && extractArk(uri)) {
    try {
      const pathname = `/ark:/61903/${extractArk(uri)}`
      const { search } = new URL(uri)
      return `${pathname}${search}`
    } catch (error) {
      return ''
    }
  }
  return ''
}

export default App
